import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Row, Column } from "@faire/web/slate/Layout";
import { Spacer, SpacerMultipliersKey } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { useViewportWidth } from "@faire/web/ui/hooks/useViewportWidth";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled from "styled-components";

import {
  DefinitionDescription,
  DefinitionTerm,
  EyebrowText,
  LimitedSectionContent,
  Section,
  SectionHeadline,
} from "@faire/retailer/views/careers/__internal__/common/common";
import {
  extendedMediaStrings,
  isExtraXLarge,
} from "@faire/retailer/views/careers/__internal__/common/media";

import { Accordion } from "./Accordion";
import { FAIRE_BENEFITS } from "./faireBenefits";

export const OurBenefitsSection = () => {
  const { strictLocalize } = useStrictLocalization();
  const [openIndex, setOpenIndex] = useState<number | undefined>(0);
  const { isTabletAndAbove, isDesktopAndAbove } = useViewport();

  const toggleAccordionItem = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(undefined);
    } else {
      setOpenIndex(index);
    }
  };

  return (
    <Section
      $withHorizontalPadding
      $withVerticalPadding
      align="center"
      id="benefits"
    >
      <LimitedSectionContent>
        <Title />
        {isTabletAndAbove ? (
          <Column align={isDesktopAndAbove ? "flex-end" : "flex-start"}>
            <Benefits />
          </Column>
        ) : (
          <Accordion
            data={FAIRE_BENEFITS(strictLocalize)}
            openIndex={openIndex}
            onItemClick={toggleAccordionItem}
          />
        )}
      </LimitedSectionContent>
    </Section>
  );
};

const Benefits = () => {
  const { strictLocalize } = useStrictLocalization();
  const benefitRows = [];
  const viewportWidth = useViewportWidth((width) => width);
  const columnSize = Math.floor(FAIRE_BENEFITS(strictLocalize).length / 2);
  for (let i = 0; i < columnSize; i++) {
    benefitRows.push({
      left: FAIRE_BENEFITS(strictLocalize)[i],
      right: FAIRE_BENEFITS(strictLocalize)[i + columnSize],
    });
  }

  const standardSpacerSize = useResponsiveValue<SpacerMultipliersKey | number>({
    mobileAndAbove: 0,
    tabletAndAbove: "9x",
    desktopAndAbove: "5x",
  });
  const spacerSize = isExtraXLarge(viewportWidth) ? "7x" : standardSpacerSize;

  return (
    <>
      {benefitRows.map((row, index) => {
        return (
          <React.Fragment key={index}>
            {index === 0 ? null : <Spacer size="5x" />}
            <BenefitsRow>
              <Benefit
                title={row.left?.title ?? ""}
                description={row.left?.description ?? ""}
                number={String(index + 1).padStart(2, "0")}
              />
              <Spacer width={spacerSize} />
              <Benefit
                title={row.right?.title ?? ""}
                description={row.right?.description ?? ""}
                number={String(index + 1 + columnSize).padStart(2, "0")}
              />
            </BenefitsRow>
          </React.Fragment>
        );
      })}
    </>
  );
};

const TitleBase = () => {
  const regularBottomSpacer = useResponsiveValue<SpacerMultipliersKey | number>(
    {
      mobileAndAbove: "4x",
      tabletAndAbove: "5x",
      desktopAndAbove: "7x",
    }
  );
  const viewportWidth = useViewportWidth((width) => width);

  const bottomSpace = isExtraXLarge(viewportWidth) ? 90 : regularBottomSpacer;

  return (
    <div style={{ maxWidth: "450px" }}>
      <EyebrowText>
        <StrictLocalMsg
          defaultMessage="Our Benefits"
          description={{
            text: "The heading for a section on a careers page listing the company's employee benefits",
          }}
        />
      </EyebrowText>
      <Spacer height="3x" />
      <SectionHeadline>
        <StrictLocalMsg
          defaultMessage="Some of the perks and experiences you’ll have as an employee at Faire"
          description={{
            text: "Subheading for a section listing the company's employee benefits",
          }}
        />
      </SectionHeadline>
      <Spacer height={bottomSpace} />
    </div>
  );
};

const Title = observer(TitleBase);

interface BenefitProps {
  title: string;
  description: string;
  number: string;
}

const BenefitBase = ({ title, description, number }: BenefitProps) => {
  const viewport = useViewport();
  return (
    <Row align="flex-start">
      {viewport.isDesktopAndAbove ? (
        <>
          <Typography variant="subheadingSansMedium">{number}</Typography>
          <Spacer size="2x" />
        </>
      ) : null}
      <dl>
        <BenefitTitle>{title}</BenefitTitle>
        <Spacer size="1x" />
        <BenefitDescription>{description}</BenefitDescription>
      </dl>
    </Row>
  );
};

const Benefit = observer(BenefitBase);

const BenefitsRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;

  ${responsiveMediaQueries.tablet} {
    justify-content: center;
  }
`;

const BenefitTitle = styled(DefinitionTerm)`
  width: 100%;

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 346px;
  }
`;

const BenefitDescription = styled(DefinitionDescription)`
  width: 260px;
  ${extendedMediaStrings.extraxlarge} {
    width: 346px;
  }
`;
